import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./css/FileUpload.css";
import "./css/WorkflowDetails.css";
import Modal from "react-modal";
import API_BASE from "./config";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";
import { normalizePrograms } from "./utils/normalizePrograms"; // ✅ Import normalization function

const WorkflowDetails = ({ chatId, cognitoUserId, workflowMetadataId, organization, launchable = true }) => {
    const [workflowData, setWorkflowData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [svgContent, setSvgContent] = useState("");
    const [svgUrl, setSvgUrl] = useState("");
    const location = useLocation();
    const [nfFile, setNfFile] = useState("");
    const [tentativeCharge, setTentativeCharge] = useState(null);
    
    useEffect(() => {
        const fetchWorkflowDetails = async () => {
            try {
                const response = await fetch(`${API_BASE}/get-workflow-details/${workflowMetadataId}`);
                const data = await response.json();

                const details = data.workflowDetails[0];
                setWorkflowData(details);

                if (details?.nextflow_path) {
                    setNfFile(details.nextflow_path);
                }

                setLoading(false);

                if (details?.workflow_svg_path) {
                    const svgResponse = await fetch(`${API_BASE}/${details.workflow_svg_path}`);
                    if (svgResponse.ok) {
                        const svgText = await svgResponse.text();
                        setSvgContent(svgText);
                    }
                }

                const svgFolder = details?.workflow_svg_path?.split("/").slice(0, -1).join("/");
                const mainSvgPath = `${svgFolder}/main.svg`;
                try {
                    const mainSvgResponse = await fetch(`${API_BASE}/${mainSvgPath}`);
                    if (mainSvgResponse.ok) {
                        setSvgUrl(`${API_BASE}/${mainSvgPath}`);
                    }
                } catch (err) {
                    console.warn("main.svg not found:", err);
                }
            } catch (error) {
                console.error("Error fetching workflow details or SVG file content:", error);
                setError("Failed to load workflow details.");
                setLoading(false);
            }
        };
        fetchWorkflowDetails();
    }, [workflowMetadataId]);

    const NFVIZ_BASE = process.env.REACT_APP_NFVIZ_BASE;

    // Absolute root where NFViz expects workflow folders
    const NFVIZ_ROOT = "/home/apps/cloudgenomics/backend";

    let workflowFolder = null;

    if (workflowData?.nextflow_path) {
    // Remove /main.nf
    const relativeFolder = workflowData.nextflow_path.replace(/\/main\.nf$/, "");
    // Prepend the absolute filesystem root
    workflowFolder = `${NFVIZ_ROOT}${relativeFolder}`;
    }

    // Designer (and BYOP) workflows open the applet in full EDITOR mode (editor.html);
    // app-generated/imported workflows keep the default viewer (embed.html).
    const editorMode = ["designer", "byop"].includes(
        String(workflowData?.workflow_type || "").toLowerCase()
    );
    const appletPage = editorMode ? "editor.html" : "embed.html";

    const embedUrl =
    NFVIZ_BASE && workflowFolder
        ? `${NFVIZ_BASE}/${appletPage}?type=local&theme=light&edit=on&grid=on&source=${encodeURIComponent(
            workflowFolder
        )}`
        : null;

    if (loading) {
        return <p className="workflow-loading-text">Loading workflow and other details...</p>;
    }

    if (error) {
        return <p className="workflow-error-text">{error}</p>;
    }

    // Derive validation state from the fetched workflow row (SELECT * returns
    // validated_final), so the upload section is correctly hidden until validated
    // on EVERY render path — not only when a `launchable` prop is passed. Also
    // honour the prop (a parent may force it off) so both signals must agree.
    const isValidated = workflowData?.validated_final === "Y" && launchable;

    return (
        <div className="workflow-details-page">
            <div className="workflow-header">
                <h3 className="workflow-title">
                    {isValidated
                        ? "Your workflow has been created. To launch analysis, upload the files."
                        : "Your workflow has been created and is being validated. File upload and launch will be available once validation completes."}
                </h3>
            </div>

            <section className="workflow-section">
                <h4 className="workflow-section-title">Workflow Summary</h4>
                {workflowData?.workflow_summary ? (
                    <div
                        className="workflow-summary"
                        dangerouslySetInnerHTML={{ __html: workflowData.workflow_summary }}
                    />
                ) : (
                    <p className="workflow-muted-text">No workflow summary available.</p>
                )}
            </section>

            <section className="workflow-section">
            <h4 className="workflow-section-title">Workflow Diagram</h4>

            {embedUrl ? (
                <div className="workflow-diagram-container">
                <iframe
                    src={embedUrl}
                    title="Nextflow workflow"
                    className="workflow-diagram-iframe"
                    style={{
                    width: "100%",
                    height: "600px",
                    border: 0,
                    borderRadius: 8,
                    background: "#fff"
                    }}
                    loading="lazy"
                />
                </div>
            ) : svgContent || svgUrl ? (
                <div className="workflow-diagram-container">
                {svgContent && (
                    <pre className="workflow-diagram-raw">{svgContent}</pre>
                )}
                {svgUrl && (
                    <div className="workflow-diagram-image-wrapper">
                    <img
                        src={svgUrl}
                        alt="Workflow DAG"
                        className="workflow-diagram-image"
                    />
                    </div>
                )}
                </div>
            ) : (
                <p className="workflow-muted-text">Workflow diagram not available.</p>
            )}
            </section>


            <section className="workflow-section">
                <h4 className="workflow-section-title">Programs Used in Analysis</h4>
                {(() => {
                    const steps = normalizePrograms(workflowData?.containers_list);

                    if (!steps.length) {
                        return <p className="workflow-muted-text">No containers listed for this workflow.</p>;
                    }

                    return (
                        <table className="workflow-programs-table">
                            <thead>
                                <tr>
                                    <th className="workflow-programs-header-step">STEP</th>
                                    <th className="workflow-programs-header-programs">PROGRAMS USED</th>
                                </tr>
                            </thead>
                            <tbody>
                                {steps.map((step, index) => (
                                    <tr key={index}>
                                        <td className="workflow-programs-step-cell">
                                            {step.name}
                                        </td>
                                        <td className="workflow-programs-programs-cell">
                                            {step.programs.map((p, i) => (
                                                <div key={i} className="workflow-program-entry">
                                                    <strong>{p.name}</strong>
                                                    {p.version && ` (v${p.version})`}
                                                    {p.default_parameters && (
                                                        <div className="workflow-program-default-params">
                                                            Default params: {p.default_parameters}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
            </section>

            {/* Upload section is hidden entirely until the workflow is validated
                (validated_final === 'Y'). Until then, "Programs Used in Analysis"
                is the last visible section. */}
            {isValidated && (
                <FileUploadSection
                    inputFiles={workflowData?.input_files_list?.split("|") || []}
                    workflowMetadataId={workflowMetadataId}
                    cognitoUserId={cognitoUserId}
                    chatId={chatId}
                    organization={organization}
                    nfFile={nfFile}
                    launchable={isValidated}
                />
            )}
        </div>
    );
};

const FileUploadSection = ({ inputFiles, workflowMetadataId, cognitoUserId, organization, nfFile, launchable = true }) => {
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadStatus, setUploadStatus] = useState("");
    const folderPathRef = useRef(null);
    const [allUploadsCompleted, setAllUploadsCompleted] = useState(false);
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [tentativeCharge, setTentativeCharge] = useState(null);
    const [userCredits, setUserCredits] = useState(null);
    const [uploadedFilesBySection, setUploadedFilesBySection] = useState({}); // {sectionIndex: [{file, id}]}

    const handleLaunchAnalysis = () => {
        if (!cognitoUserId) return;
        if (!launchable) return; // workflow still under validation / failed validation
        setModalOpen(true);
    };

    const confirmLaunch = async () => {
        setModalOpen(false);
        const payload = {
            workflow_id: workflowMetadataId,
            cognitoUserId,
            s3FolderPath: folderPathRef.current,
            organization
        };
        axios
            .post(`${API_BASE}/launch-analysis`, payload)
            .then(() => {
                // Launch successful
            })
            .catch((error) => {
                console.error("❌ Error launching analysis:", error);
            });
        navigate(`/analyses`);
    };

    useEffect(() => {
        document.addEventListener("dragover", (event) => {
            event.preventDefault();
            event.stopPropagation();
        });
        document.addEventListener("drop", (event) => {
            event.preventDefault();
            event.stopPropagation();
        });

        if (!folderPathRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:.]/g, "_");
            // New layout: launched runs live under the user's Analysis/ folder.
            folderPathRef.current = `${cognitoUserId}/Analysis/${timestamp}-${workflowMetadataId}`;
        }
    }, [cognitoUserId, workflowMetadataId]);

    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const res = await fetch(`${API_BASE}/user/credits`, {
                    credentials: "include"
                });
                const data = await res.json();
                setUserCredits(data.balance);
            } catch (err) {
                console.error("Error fetching user credits:", err);
                setUserCredits(0);
            }
        };

        fetchCredits();
    }, []);

    const insufficientCredits = userCredits !== null && userCredits < 50;

    const removeFileFromSection = (sectionIndex, entryId) => {
        const entries = uploadedFilesBySection[sectionIndex] || [];
        const entry = entries.find((e) => e.id === entryId);

        setUploadProgress((prev) => {
            const currentSection = prev[`section_${sectionIndex}`] || {};
            const { [entry?.file?.name || ""]: _, ...restSection } = currentSection;
            return {
                ...prev,
                [`section_${sectionIndex}`]: restSection
            };
        });

        setUploadedFilesBySection((prev) => ({
            ...prev,
            [sectionIndex]: entries.filter((e) => e.id !== entryId)
        }));
    };

    const SectionDropzone = ({ sectionIndex, label }) => {
        const onDrop = (acceptedFiles) => {
            const newEntries = acceptedFiles.map((file) => ({
                file,
                id: uuidv4()
            }));
            setUploadedFilesBySection((prev) => ({
                ...prev,
                [sectionIndex]: [...(prev[sectionIndex] || []), ...newEntries]
            }));
        };

        const { getRootProps, getInputProps } = useDropzone({
            onDrop,
            multiple: true
        });

        const filesForSection = uploadedFilesBySection[sectionIndex] || [];

        return (
            <div className="upload-section">
                <label className="upload-section-label">{label.trim()}</label>
                <div
                    {...getRootProps({
                        disabled: insufficientCredits
                    })}
                    className="upload-dropzone"
                >
                    <input {...getInputProps()} />
                    <p className="upload-dropzone-text">
                        Drag &amp; drop files here or click to select.
                    </p>

                    <div className="upload-file-cards">
                        {filesForSection.map((entry) => {
                            const fileName = entry.file.name;
                            const sectionProgress = uploadProgress[`section_${sectionIndex}`] || {};
                            const progress = sectionProgress[fileName] ?? 0;
                            const isError = progress < 0;

                            return (
                                <div
                                    key={entry.id}
                                    className="upload-file-card"
                                >
                                    <strong className="upload-file-name">{fileName}</strong>
                                    <div className="upload-progress-bar">
                                        <div
                                            className={`upload-progress-bar-fill ${
                                                isError ? "upload-progress-bar-fill-error" : ""
                                            }`}
                                            style={{ width: `${Math.max(0, progress)}%` }}
                                        />
                                    </div>
                                    <div
                                        className={`upload-progress-text ${
                                            isError ? "upload-progress-text-error" : ""
                                        }`}
                                    >
                                        {isError ? "Upload failed" : `${progress}%`}
                                    </div>
                                    <button
                                        onClick={() => removeFileFromSection(sectionIndex, entry.id)}
                                        className="upload-remove-button"
                                    >
                                        Remove File
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <button
                    onClick={() => handleUpload(sectionIndex)}
                    className="upload-primary-button"
                    disabled={insufficientCredits}
                >
                    Upload {label}
                </button>
            </div>
        );
    };

        const handleUpload = async (index) => {
        setUploadStatus(`Uploading files for section ${index + 1}...`);
        const folderPath = folderPathRef.current;
        const entries = uploadedFilesBySection[index] || [];
        const files = entries.map((e) => e.file);

        if (!files.length) {
            console.error(`No files selected for section ${index}.`);
            return;
        }

        const updatedProgress = { ...uploadProgress };

        await Promise.all(
            files.map(async (file) => {
                if (file.size <= 5 * 1024 * 1024) {
                    await uploadSmallFileToS3(file, folderPath, index);
                } else {
                    await uploadLargeFileToS3(file, folderPath, index);
                }
                await storeFileInDatabase(workflowMetadataId, file.name, folderPath);

                updatedProgress[`section_${index}`] = {
                    ...updatedProgress[`section_${index}`],
                    [file.name]: 100
                };

                inputFiles.forEach((_, i) => {
                    if (!updatedProgress[`section_${i}`]) {
                        updatedProgress[`section_${i}`] = {};
                    }
                });

                setUploadProgress(updatedProgress);
            })
        );

        try {
            const response = await axios.get(`${API_BASE}/read-nextflow-file?filePath=${nfFile}`);
            const nextflowFileContent = response.data.fileContent;
            const nextflowFile = new File([nextflowFileContent], "main.nf", { type: "text/plain" });

            await uploadSmallFileToS3(nextflowFile, folderPath, 0);
            updatedProgress["nextflow_file"] = { "main.nf": 100 };

            const configRelativePath = `${cognitoUserId}_${workflowMetadataId}/nextflow.config`;
            const configResponse = await axios.get(
                `${API_BASE}/read-config-file?filePath=${encodeURIComponent(configRelativePath)}`
            );
            const configContent = configResponse.data.fileContent;
            const configFileObj = new File([configContent], "nextflow.config", { type: "text/plain" });

            await uploadSmallFileToS3(configFileObj, folderPath, 0);
            updatedProgress["config_file"] = { "nextflow.config": 100 };
        } catch (error) {
            console.error("❌ Error uploading main.nf or nextflow.config:", error);
        }

        setTimeout(() => checkIfAllUploadsCompleted(updatedProgress), 100);
    };

    const checkIfAllUploadsCompleted = async (latestProgress) => {
        const allSectionsCompleted = inputFiles.every((_, index) => {
            const sectionProgress = latestProgress[`section_${index}`] || {};
            return (
                Object.values(sectionProgress).length > 0 &&
                Object.values(sectionProgress).every((progress) => progress === 100)
            );
        });

        const nextflowUploaded = latestProgress["nextflow_file"]?.["main.nf"] === 100;

        if (allSectionsCompleted && nextflowUploaded) {
            //setUploadStatus("ALL FILES UPLOADED SUCCESSFULLY");
            setAllUploadsCompleted(true);
        }
    };

    const updateProgress = (file, progress, index) => {
        setUploadProgress((prev) => ({
            ...prev,
            [`section_${index}`]: {
                ...prev[`section_${index}`],
                [file.name]: Math.round(progress)
            }
        }));
    };

    const uploadSmallFileToS3 = async (file, folderPath, index) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folderPath", folderPath);
        await axios.post(`${API_BASE}/upload-small-file`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            maxContentLength: Infinity,
            maxBodyLength: Infinity
        });
        updateProgress(file, 100, index);
    };

    const uploadLargeFileToS3 = async (file, folderPath, index) => {
        const CHUNK_SIZE = 5 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const multipartUpload = await axios.post(`${API_BASE}/initiate-multipart-upload`, {
            fileName: file.name,
            folderPath: folderPath
        });
        const uploadId = multipartUpload.data.uploadId;
        const uploadedParts = [];
        for (let i = 0; i < totalChunks; i++) {
            let chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const formData = new FormData();
            formData.append("uploadId", uploadId);
            formData.append("partNumber", i + 1);
            formData.append("fileName", file.name);
            formData.append("folderPath", folderPath);
            formData.append("totalChunks", totalChunks);
            formData.append("fileChunk", chunk);
            const partUpload = await axios.post(`${API_BASE}/upload-part`, formData);
            uploadedParts.push({ ETag: partUpload.data.ETag, PartNumber: i + 1 });
            updateProgress(file, ((i + 1) / totalChunks) * 100, index);
        }
        await axios.post(`${API_BASE}/complete-multipart-upload`, {
            uploadId: uploadId,
            parts: uploadedParts,
            fileName: file.name,
            folderPath: folderPath
        });
        updateProgress(file, 100, index);
    };

    const storeFileInDatabase = async (workflowId, fileName, folderPath) => {
        try {
            await axios.post(`${API_BASE}/store-file`, {
                workflow_id: workflowId,
                filename: fileName,
                path: folderPath
            });
        } catch (error) {
            console.error(`Error storing file ${fileName} in database:`, error);
        }
    };

    useEffect(() => {
        const fetchTentativeCharge = async () => {
            try {
                const orgRes = await fetch(`${API_BASE}/get-organization-details-by-name/${organization}`);
                const orgData = await orgRes.json();
                console.log("ORG DATA:", orgData);

                const orgId = orgData.organization.id;

                const priceRes = await fetch(
                    `${API_BASE}/tentative-execution-charge?workflow_id=${workflowMetadataId}&organization_id=${orgId}`
                );
                const priceData = await priceRes.json();

                setTentativeCharge(priceData.tentative_per_sample);
            } catch (err) {
                console.error("Error fetching tentative charge:", err);
                setTentativeCharge(null);
            }
        };

        fetchTentativeCharge();
    }, [workflowMetadataId, organization]);

        return (
        <div className="workflow-upload-section">
            <RequirePermission permission="executeBuilds">
                <h4 className="workflow-upload-heading">Ready to analyze? Upload input files</h4>

                {tentativeCharge !== null && (
                    <p className="workflow-tentative-charge">
                        Tentative per-sample credits charge: {Math.min(tentativeCharge, 20).toFixed(2)}
                    </p>
                )}

                {inputFiles.map((fileType, index) => (
                    <SectionDropzone
                        key={index}
                        sectionIndex={index}
                        label={fileType}
                    />
                ))}

                {uploadStatus && (
                    <p className="workflow-upload-status">
                        {uploadStatus}
                    </p>
                )}

                {allUploadsCompleted && (
                    <div className="workflow-launch-container">
                        <p className="workflow-all-complete-text">
                            ALL FILES UPLOADED SUCCESSFULLY
                        </p>
                        <button
                            onClick={handleLaunchAnalysis}
                            className="workflow-launch-button"
                            disabled={insufficientCredits || !launchable}
                            title={!launchable ? "This workflow is still being validated and can't be launched yet." : undefined}
                        >
                            LAUNCH ANALYSIS
                        </button>
                        {!launchable && (
                            <p style={{ fontSize: "13px", fontStyle: "italic", color: "#92400e", marginTop: "6px" }}>
                                ⏳ Launching will be enabled once automated validation completes.
                            </p>
                        )}
                    </div>
                )}

                <Modal
                    isOpen={modalOpen}
                    onRequestClose={() => setModalOpen(false)}
                    className="workflow-modal"
                    overlayClassName="workflow-modal-overlay"
                >
                    <h2 className="workflow-modal-title">Launch Analysis</h2>
                    <p className="workflow-modal-body">
                        THIS ACTION WILL LAUNCH ANALYSIS OF THE SELECTED SAMPLES ON CLOUD. DO YOU WANT TO PROCEED?
                    </p>
                    {tentativeCharge !== null && (
                        <p className="workflow-modal-charge">
                            Tentative per-sample credits: {tentativeCharge.toFixed(2)}
                        </p>
                    )}

                    <div className="workflow-modal-buttons">
                        <button
                            onClick={confirmLaunch}
                            className="workflow-modal-button workflow-modal-button-confirm"
                        >
                            YES
                        </button>
                        <button
                            onClick={() => setModalOpen(false)}
                            className="workflow-modal-button workflow-modal-button-cancel"
                        >
                            NO
                        </button>
                    </div>
                </Modal>
                {insufficientCredits && (
                    <p className="workflow-insufficient-credits-alert">
                        You have less than 50 Credits available. At least 50 credits are required to launch an analysis.
                    </p>
                )}
            </RequirePermission>
        </div>
    );
};

export default WorkflowDetails;